import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { MdOutlineSettings } from "react-icons/md";
import RfqBoqItemRow from "./RfqBoqItemRow";

const isCostPending = (boq) => {
  const price = boq?.type === "Activities" ? boq?.manpower_rate : boq?.unit_cost;
  return price == null || price === "";
};

const BOQTabsView = ({
  activeTab,
  setActiveTab,
  roomBOQs,
  categoryData,
  boqPriceInputs,
  savingBoqId,
  handleSaveBOQPrice,
  handleBOQPriceChange,
  fetchCategoryData,
  fetchRoomData,
  isDisabled = false,
}) => {
  const [pendingOnly, setPendingOnly] = useState(false);

  const handleTabChange = async (e, newValue) => {
    setActiveTab(newValue);
    if (newValue === 1) {
      await fetchCategoryData();
    } else if (newValue === 0) {
      await fetchRoomData();
    }
  };

  const renderRow = (boq, roomName, autoFocus = false) => (
    <RfqBoqItemRow
      key={boq.id}
      boq={boq}
      roomName={roomName}
      boqPriceInputs={boqPriceInputs}
      isBOQPriceLoading={savingBoqId === boq.id}
      isDisabled={isDisabled}
      onSave={handleSaveBOQPrice}
      onChange={handleBOQPriceChange}
      autoFocus={autoFocus}
    />
  );

  const filterBoqs = (boqs) =>
    pendingOnly ? (boqs || []).filter(isCostPending) : boqs || [];

  const countRoomPending = (room) =>
    (room?.inclusions || []).reduce(
      (sum, inc) => sum + (inc?.boqs || []).filter(isCostPending).length,
      0
    );

  const PendingBadge = ({ count }) =>
    count > 0 ? (
      <span
        style={{
          fontSize: 11,
          fontWeight: 500,
          color: "#B91C1C",
          backgroundColor: "#FEE2E2",
          padding: "2px 8px",
          borderRadius: 999,
          marginLeft: 8,
        }}
      >
        {count} pending
      </span>
    ) : null;

  return (
    <Card
      sx={{
        borderRadius: "12px",
        boxShadow: "none",
        margin: "1.5vh 0",
        border: "1px solid #E5E7EB",
        overflow: "initial",
      }}
    >
      <CardContent sx={{ padding: "16px" }}>
        <Box
          sx={{
            display: "flex",
            gap: "16px",
            marginBottom: "12px",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              minHeight: 0,
              "& .MuiTabs-indicator": { backgroundColor: "#4D6BF6" },
            }}
          >
            <Tab
              label="By Room"
              sx={{
                minHeight: 0,
                padding: "8px 12px",
                color: activeTab === 0 ? "#4D6BF6" : "#6B7280",
                fontWeight: activeTab === 0 ? 600 : 400,
                textTransform: "none",
              }}
            />
            <Tab
              label="By Category"
              sx={{
                minHeight: 0,
                padding: "8px 12px",
                color: activeTab === 1 ? "#4D6BF6" : "#6B7280",
                fontWeight: activeTab === 1 ? 600 : 400,
                textTransform: "none",
              }}
            />
          </Tabs>
          <button
            type="button"
            onClick={() => setPendingOnly((v) => !v)}
            style={{
              padding: "6px 12px",
              fontSize: 12,
              fontWeight: 500,
              borderRadius: 8,
              border: pendingOnly ? "1px solid #EF4444" : "1px solid #D1D5DB",
              backgroundColor: pendingOnly ? "#FEE2E2" : "#fff",
              color: pendingOnly ? "#B91C1C" : "#374151",
              cursor: "pointer",
            }}
          >
            {pendingOnly ? "Showing Unpriced BOQs" : "Show Unpriced BOQs"}
          </button>
        </Box>

        {activeTab === 0 && (
          <div>
            {roomBOQs?.rooms?.map((room, roomIndex) => {
              const pendingCount = countRoomPending(room);
              return (
                <Accordion
                  key={roomIndex}
                  defaultExpanded={true}
                  sx={{ marginBottom: 0, boxShadow: "none" }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 15,
                        color: "#0d0d0e",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <div>🏠</div>
                      <div>{room.room_name}</div>
                      <PendingBadge count={pendingCount} />
                    </div>
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: "0 8px 12px" }}>
                    {room.inclusions?.map((inclusion, inclusionIndex) => {
                      const filtered = filterBoqs(inclusion.boqs);
                      if (pendingOnly && filtered.length === 0) return null;
                      return (
                        <Box key={inclusionIndex} sx={{ marginBottom: "12px" }}>
                          <div
                            style={{
                              fontWeight: 500,
                              fontSize: 13,
                              color: "#6B7280",
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              marginBottom: "8px",
                            }}
                          >
                            <MdOutlineSettings size={14} />
                            {inclusion.name}
                          </div>
                          {filtered.map((boq) => renderRow(boq))}
                        </Box>
                      );
                    })}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </div>
        )}

        {activeTab === 1 && (
          <Box sx={{ boxShadow: "none" }}>
            {categoryData?.inclusions?.map((inclusion, inclusionIndex) => {
              const inclusionPending = (inclusion.rooms || []).reduce(
                (sum, r) => sum + (r.boqs || []).filter(isCostPending).length,
                0
              );
              return (
                <Accordion
                  key={inclusionIndex}
                  defaultExpanded={true}
                  sx={{ marginBottom: 0, boxShadow: "none" }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 15,
                        color: "#0d0d0e",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <MdOutlineSettings />
                      <div>{inclusion.name}</div>
                      <PendingBadge count={inclusionPending} />
                      <Typography variant="caption" color="text.secondary">
                        Used in:{" "}
                        {inclusion.rooms?.map((r) => r.name).join(", ")}
                      </Typography>
                    </div>
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: "0 8px 12px" }}>
                    {inclusion.rooms?.map((room, roomIndex) => {
                      const filtered = filterBoqs(room.boqs);
                      if (pendingOnly && filtered.length === 0) return null;
                      return (
                        <Box key={roomIndex} sx={{ marginBottom: "12px" }}>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 500, marginBottom: "6px" }}
                          >
                            {room.name}
                          </Typography>
                          {filtered.map((boq) => renderRow(boq, room.name))}
                        </Box>
                      );
                    })}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default BOQTabsView;
